import { computeArea, LatLng } from 'spherical-geometry-js'
import { tokens } from 'styling/tokens'
import { ILocationNode } from 'types/locationsRegistry.interface'

export const TYPE_META: { [key: string]: { label: string } } = {
  SITE: { label: 'Site' },
  AREA: { label: 'Area' },
  ZONE: { label: 'Zone' },
  SECTION: { label: 'Section' },
  BLOCK: { label: 'Block' },
  PIT: { label: 'Pit' },
  STOCKPILE: { label: 'Stockpile' },
  DUMP: { label: 'Waste Dump' },
  PLANT_SECTION: { label: 'Plant Section' },
  BUILDING: { label: 'Building' },
  BAY: { label: 'Bay' },
  FUEL_STORAGE: { label: 'Fuel Storage' },
  CAMP_BLOCK: { label: 'Camp Block' },
  ROOM: { label: 'Room' },
  TSF: { label: 'TSF / Water' },
  MUSTER: { label: 'Muster Point' },
  ROAD: { label: 'Road' },
}

export const CHILD_TYPES: { [key: string]: string[] } = {
  SITE: ['AREA'],
  AREA: [
    'ZONE',
    'STOCKPILE',
    'DUMP',
    'PLANT_SECTION',
    'BUILDING',
    'FUEL_STORAGE',
    'CAMP_BLOCK',
    'TSF',
    'MUSTER',
    'ROAD',
  ],
  ZONE: ['SECTION'],
  SECTION: ['BLOCK'],
  BLOCK: ['PIT'],
  BUILDING: ['BAY'],
  CAMP_BLOCK: ['ROOM'],
}

export const INDOOR_TYPES = ['ROOM', 'BAY']

export const ROADS_AREA_ID = 80

export const px2ll = (x: number, y: number): [number, number] => [
  6.6885 + (230 - y) * 0.00002,
  -1.6244 + (x - 400) * 0.00002,
]

export const fmtLL = ([la, ln]: [number, number]) => `${la.toFixed(5)}, ${ln.toFixed(5)}`

export const toLatLngPath = (pts: number[][]) => pts.map(([lat, lng]) => ({ lat, lng }))

export const centroidOf = (pts: number[][]) => [
  pts.reduce((s, p) => s + p[0], 0) / pts.length,
  pts.reduce((s, p) => s + p[1], 0) / pts.length,
]

export const areaHa = (pts: number[][]) => {
  if (pts.length < 3) return 0
  return computeArea(pts.map(([lat, lng]) => new LatLng(lat, lng))) / 10000
}

const haversineM = (a: number[], b: number[]) => {
  const toRad = (d: number) => (d * Math.PI) / 180
  const r = 6371000
  const dLat = toRad(b[0] - a[0])
  const dLng = toRad(b[1] - a[1])
  const lat1 = toRad(a[0])
  const lat2 = toRad(b[0])
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return 2 * r * Math.asin(Math.sqrt(h))
}

export const polyLenM = (pts: number[][], i0 = 0, i1 = pts.length - 1) => {
  let length = 0
  for (let i = Math.min(i0, i1); i < Math.max(i0, i1); i++) {
    length += haversineM(pts[i], pts[i + 1])
  }
  return length
}

export const inPoly = (pt: number[], pts: number[][]) => {
  const [lat, lng] = pt
  let inside = false
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [yi, xi] = pts[i]
    const [yj, xj] = pts[j]
    if (yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
      inside = !inside
    }
  }
  return inside
}

export const flattenNodes = (node: ILocationNode, out: ILocationNode[] = []) => {
  out.push(node)
  ;(node.children || []).forEach((child) => flattenNodes(child, out))
  return out
}

export const buildParents = (
  node: ILocationNode,
  map: { [id: number]: number } = {},
  parentId: number | null = null,
) => {
  if (parentId !== null) map[node.id] = parentId
  ;(node.children || []).forEach((child) => buildParents(child, map, node.id))
  return map
}

export const insertNode = (
  root: ILocationNode,
  parentId: number,
  newNode: ILocationNode,
): ILocationNode => {
  if (root.id === parentId) {
    return { ...root, children: [...root.children, newNode] }
  }
  return { ...root, children: root.children.map((child) => insertNode(child, parentId, newNode)) }
}

export const STATUS_COLORS: { [key: string]: string } = {
  OPERATIONAL: tokens.status.success.fg,
  ACTIVE: tokens.status.success.fg,
  HELD: tokens.accent.base,
  EVACUATED: tokens.status.danger.fg,
  DEPLETED: tokens.text.muted,
  PLANNED: tokens.status.info.fg,
  RESTRICTED: tokens.status.info.fg,
  Statutory: tokens.status.info.fg,
  OK: tokens.status.success.fg,
  Unaccounted: tokens.status.danger.fg,
  Accounted: tokens.status.success.fg,
  Drill: tokens.accent.base,
  MOBILE: tokens.status.info.fg,
}

export const MATERIAL_OPTIONS = [
  'Ore (run-of-mine)',
  'Ore · high grade',
  'Ore · low grade',
  'Overburden',
]
export const SURFACE_OPTIONS = ['Laterite', 'Gravel', 'Compacted earth', 'Sealed']
export const FUEL_KIND_OPTIONS = ['Fixed', 'Mobile (bowser)']

export const getModuleBindings = (node: ILocationNode): [string, string][] => {
  const bindings: [string, string][] = []
  if (node.attrs?.inventoryLocation) {
    bindings.push(['Inventory', String(node.attrs.inventoryLocation)])
  }
  if (node.id === 14 || node.id === 15) {
    bindings.push(['Operations', 'Plan zoneScope · haulage cycle origin'])
    bindings.push(['Fuel', 'Refuel events at node'])
  }
  if (node.id === 43) {
    bindings.push(['Machinery', 'CAT320 current location'])
  }
  if (node.id === 47) {
    bindings.push(['Food', 'Catchment target · kitchen entity'])
    bindings.push(['Kitchen Mgmt', 'Consumption postings'])
  }
  if (node.id === 61) {
    bindings.push(['Operations', 'Daily statutory inspection (SCHEDULED trigger)'])
  }
  return bindings
}
